import { saveAs } from 'file-saver';
import { apiService } from '../services/apiService';
import { showToast } from '../store/toastStore';
import { useExportUiStore } from '../store/exportUiStore';
import { API_PATHS } from './apiPaths';

export const EXPORT_TYPES = {
  ORDERS: 'ORDERS',
  MERCHANTS: 'MERCHANTS',
  ACQUIRERS: 'ACQUIRERS',
  USERS: 'USERS',
  TERMINALS: 'TERMINALS',
  MERCHANT_TERMINALS: 'MERCHANT_TERMINALS',
  ACQUIRER_TERMINALS: 'ACQUIRER_TERMINALS',
  STOREFRONT_URLS: 'STOREFRONT_URLS',
  AUDIT_LOGS: 'AUDIT_LOGS',
} as const;

export type BackendExportType = (typeof EXPORT_TYPES)[keyof typeof EXPORT_TYPES];

/** Backend export ignores client Excel building; send a wide page so filters return the full sheet. */
export const EXPORT_PAGE_SIZE = 10000;

function parseFilename(contentDisposition?: string): string | null {
  if (!contentDisposition) return null;
  const utfMatch = contentDisposition.match(/filename\*=(?:UTF-8'')?([^;]+)/i);
  if (utfMatch?.[1]) {
    try {
      return decodeURIComponent(utfMatch[1].trim().replace(/^["']|["']$/g, ''));
    } catch {
      return utfMatch[1].trim().replace(/^["']|["']$/g, '');
    }
  }
  const basicMatch = contentDisposition.match(/filename=([^;]+)/i);
  if (!basicMatch?.[1]) return null;
  return basicMatch[1].trim().replace(/^["']|["']$/g, '');
}

async function readBlobJsonMessage(blob: Blob): Promise<string | null> {
  const text = await blob.text();
  if (!text.trim()) return null;
  try {
    const json = JSON.parse(text) as { message?: string; error?: string };
    return json.message || json.error || null;
  } catch {
    return null;
  }
}

export async function getBackendExportErrorMessage(err: unknown, fallback: string): Promise<string> {
  if (err && typeof err === 'object' && 'response' in err) {
    const data = (err as { response?: { data?: unknown } }).response?.data;
    if (data instanceof Blob) {
      const message = await readBlobJsonMessage(data);
      if (message) return message;
    }
    const jsonMessage = (err as { response?: { data?: { message?: string } } }).response?.data?.message;
    if (jsonMessage) return jsonMessage;
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

export async function withExcelExportFeedback(
  task: () => Promise<void>,
  messages?: { success?: string; error?: string },
): Promise<void> {
  useExportUiStore.getState().setExporting(true);
  let errorMessage: string | null = null;
  try {
    await task();
  } catch (err: unknown) {
    errorMessage = await getBackendExportErrorMessage(
      err,
      messages?.error ?? 'Failed to export Excel.',
    );
  } finally {
    useExportUiStore.getState().setExporting(false);
  }

  if (errorMessage) {
    showToast(errorMessage, 'error');
    return;
  }

  showToast(messages?.success ?? 'Excel exported successfully.', 'success');
}

export async function downloadExcelExport(options: {
  type: BackendExportType;
  fileNamePrefix: string;
  payload?: object;
  successMessage?: string;
  errorMessage?: string;
}): Promise<void> {
  await withExcelExportFeedback(async () => {
    const rest: Record<string, unknown> = { ...(options.payload as Record<string, unknown> | undefined ?? {}) };
    delete rest.export;
    const body: Record<string, unknown> = {
      ...rest,
      type: options.type,
      page: 0,
      size: EXPORT_PAGE_SIZE,
    };

    const response = await apiService.postBlob(API_PATHS.EXPORT, body);
    const blob = response.data;

    if (blob.type.includes('application/json')) {
      const message = await readBlobJsonMessage(blob);
      throw new Error(message || 'Export failed.');
    }

    if (blob.size === 0) {
      throw new Error('No rows match the current filters to export.');
    }

    const fileName =
      parseFilename(response.headers['content-disposition']) ??
      `${options.fileNamePrefix}-${Date.now()}.xlsx`;

    saveAs(blob, fileName);
  }, {
    success: options.successMessage,
    error: options.errorMessage,
  });
}
